import { useCallback, useEffect, useState } from 'react';

import { mockReceipts } from '../mock-data';
import type { PayrollReceipt } from '../types';

const SIMULATED_DELAY_MS = 400;

/** Set to true (e.g. via ?simulateError=1) to test error state. */
const getSimulateError = (): boolean =>
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).get('simulateError') === '1';

export type UsePayrollReceiptsResult = {
  data: PayrollReceipt[] | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
};

export function usePayrollReceipts(): UsePayrollReceiptsResult {
  const [data, setData] = useState<PayrollReceipt[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchReceipts = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      await new Promise(resolve => setTimeout(resolve, SIMULATED_DELAY_MS));
      if (getSimulateError()) {
        throw new Error('Simulated error');
      }
      setData(mockReceipts);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Unknown error'));
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReceipts();
  }, [fetchReceipts]);

  return { data, isLoading, error, refetch: fetchReceipts };
}
